import { getDisplayName } from '@cascade/core/auth';
import {
  formatCalendarDateTime,
  isTextLengthWithin,
} from '@cascade/core/utils';
import { useConversationMessages } from '@cascade/react/conversations';
import { useI18n } from '@cascade/react/i18n';
import { useTickets } from '@cascade/react/tickets';
import { useUser } from '@cascade/react/user';
import {
  Badge,
  Breadcrumbs,
  Button,
  Chat,
  ChatMessages,
  Loading,
  Text,
  useToast,
} from '@cascade/ui-library';
import type { TChatMessageProps, TChatRef } from '@cascade/ui-library';
import { useMemo, useRef, useState } from 'react';
import { ReasonDialog } from './reason-dialog/index.ts';
import {
  buildThreadMessage,
  getFirstTextMessageIndex,
  MINIMUM_TICKET_MESSAGE_LENGTH,
} from './Ticket.helpers.ts';
import classes from './Ticket.module.scss';
import type { TTicketStatusChange } from './Ticket.types.ts';
import { Page } from '#components/page/index.ts';
import { TICKETS_PATH } from '#config/index.ts';
import { buildConversationMessage } from '#helpers/index.ts';

export const Ticket = () => {
  const { locale, translate, translateError } = useI18n();
  const { toast } = useToast();
  const { user } = useUser();
  const {
    currentTicket,
    currentTicketMessages,
    currentTicketConversationId,
    isLoadingCurrentTicket,
    currentTicketError,
    isUpdatingTicket,
    updateTicket,
  } = useTickets();

  const [isReasonDialogOpen, setIsReasonDialogOpen] = useState(false);
  const chatRef = useRef<TChatRef>(null);

  const userId = user?.id ?? '';
  const userName = getDisplayName(user);
  const selfName = translate('ticket.you');
  const supportName = translate('ticket.support');
  const agentName = translate('agent.name');
  const displayId = currentTicket?.display_id ?? '';

  const messages = useMemo(
    () => currentTicketMessages ?? [],
    [currentTicketMessages],
  );

  const isClosed = currentTicket?.status === 'closed';
  const firstTextIndex = getFirstTextMessageIndex(messages);
  const {
    messages: conversationMessages,
    isLoadingMessages: isLoadingConversation,
    messagesError: conversationError,
  } = useConversationMessages(currentTicketConversationId);

  const isLoading = isLoadingCurrentTicket || isLoadingConversation;
  const hasConversationFailed = conversationError !== undefined;
  const errorMessage = currentTicketError
    ? translateError(currentTicketError)
    : undefined;

  const conversationChatMessages = useMemo(
    () =>
      conversationMessages.map((message) =>
        buildConversationMessage(message, { userName, agentName }),
      ),
    [conversationMessages, userName, agentName],
  );

  const threadMessages = useMemo(
    () =>
      messages.map((message, index): TChatMessageProps => {
        const built = buildThreadMessage(
          message,
          { userId, userName, selfName, supportName },
          translate,
        );

        const date = (
          <Text
            type='caption'
            color='text-60'
            content={formatCalendarDateTime(message.date, { locale })}
          />
        );

        if (built.sender === 'system') {
          return {
            ...built,
            message: (
              <span className={classes.change}>
                <Text type='caption' color='text-60' content={built.message} />
                {date}
              </span>
            ),
          };
        }

        const isMine = built.sender === 'active-user';
        const isFirstText = index === firstTextIndex;
        const showConversation =
          isFirstText && conversationChatMessages.length > 0;

        const showConversationFailure = isFirstText && hasConversationFailed;

        return {
          ...built,
          beforeContent: (
            <>
              {showConversation ? (
                <ChatMessages
                  className={classes.reference}
                  messages={conversationChatMessages}
                  compact
                  disableAutoScroll
                />
              ) : null}
              {showConversationFailure ? (
                <Text
                  className={classes.reference}
                  type='caption'
                  color='text-danger'
                  content={translate('ticket.conversationFailed')}
                />
              ) : null}
              {isMine ? null : (
                <Badge label={supportName} variant='brand-darker' size='sm' />
              )}
              <Text
                type='caption'
                weight='medium'
                color='text-40'
                content={built.name}
              />
            </>
          ),
          afterContent: date,
        };
      }),
    [
      messages,
      userId,
      userName,
      selfName,
      supportName,
      firstTextIndex,
      conversationChatMessages,
      hasConversationFailed,
      translate,
      locale,
    ],
  );

  const breadcrumbs = [
    {
      label: translate('tickets.title'),
      uri: `/${TICKETS_PATH}`,
      iconType: 'ticket' as const,
    },
    { label: displayId },
  ];

  const openReasonDialog = () => setIsReasonDialogOpen(true);
  const closeReasonDialog = () => setIsReasonDialogOpen(false);

  const update = (
    message: string,
    failureKey: 'ticket.replyFailed' | 'ticket.updateFailed',
    newStatus?: TTicketStatusChange,
  ) =>
    updateTicket({ display_id: displayId, message, new_status: newStatus })
      .then(closeReasonDialog)
      .catch((error: unknown) => {
        toast({
          title: translate(failureKey),
          description: translateError(error),
          variant: 'danger',
        });

        throw error;
      });

  const reply = (message: string) => {
    if (!isTextLengthWithin(message, { min: MINIMUM_TICKET_MESSAGE_LENGTH })) {
      toast({ title: translate('ticket.replyTooShort'), variant: 'danger' });

      return;
    }

    const chat = chatRef.current;

    update(message.trim(), 'ticket.replyFailed').catch(() => {
      if (chat?.getValue()) {
        return;
      }

      chat?.setValue(message);
    });
  };

  const changeStatus = (reason: string) =>
    update(
      reason,
      'ticket.updateFailed',
      isClosed ? 'reopened' : 'closed',
    ).catch(() => undefined);

  return (
    <Page>
      <Breadcrumbs
        aria-label={translate('ticket.breadcrumbs')}
        items={breadcrumbs}
      />
      <div className={classes.ticket}>
        {errorMessage ? (
          <Text
            type='body-2'
            color='text-danger'
            role='alert'
            content={errorMessage}
          />
        ) : null}
        {isLoading ? (
          <Loading contain label={translate('pending.ticket')} />
        ) : null}
        {currentTicket && !isLoading ? (
          <Chat
            key={displayId}
            ref={chatRef}
            aria-label={translate('ticket.thread')}
            className={classes.thread}
            messages={threadMessages}
            placeholder={translate('ticket.reply')}
            disabled={isClosed || isUpdatingTicket}
            stagingArea={
              <div className={classes.staging}>
                <Button
                  variant={isClosed ? 'secondary' : 'primary'}
                  iconType={isClosed ? 'ticket' : 'check'}
                  label={translate(
                    isClosed ? 'ticket.reopen' : 'ticket.resolve',
                  )}
                  disabled={isUpdatingTicket}
                  onClick={openReasonDialog}
                />
              </div>
            }
            onSendMessage={reply}
          />
        ) : null}
      </div>
      {isReasonDialogOpen ? (
        <ReasonDialog
          title={translate(
            isClosed ? 'ticket.reopen.title' : 'ticket.resolve.title',
          )}
          description={translate(
            isClosed
              ? 'ticket.reopen.description'
              : 'ticket.resolve.description',
          )}
          submitLabel={translate(
            isClosed ? 'ticket.reopen.submit' : 'ticket.resolve.submit',
          )}
          minLength={MINIMUM_TICKET_MESSAGE_LENGTH}
          isPending={isUpdatingTicket}
          onSubmit={changeStatus}
          onClose={closeReasonDialog}
        />
      ) : null}
    </Page>
  );
};
